// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import { categoryDetailLinkListStyles } from "@guestsupply/Overrides/Widgets/Category/CategoryDetailLinkList";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { BrandCategoriesStateContext } from "@insite/client-framework/Store/Data/Brands/BrandsSelectors";
import translate from "@insite/client-framework/Translate";
import { BrandCategoryModel } from "@insite/client-framework/Types/ApiModels";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import LazyImage, { LazyImageProps } from "@insite/mobius/LazyImage";
import Link, { LinkPresentationProps, LinkProps } from "@insite/mobius/Link";
import Typography, { TypographyProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { FC, useContext } from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    title = "title",
    showImages = "showImages",
    subCategoriesToShow = "subCategoriesToShow",
    gridType = "gridType",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.title]: string;
        [fields.showImages]: boolean;
        [fields.subCategoriesToShow]: number;
        [fields.gridType]: string;
    };
}

const mapStateToProps = (state: ApplicationState) => ({});

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> & ResolveThunks<typeof mapDispatchToProps> & OwnProps;

export interface BrandDetailsCategoriesStyles {
    title?: TypographyProps;
    categoryItemRow?: GridItemProps;
    categoryItemColumn?: GridItemProps;
    innerCategoryContainer?: GridContainerProps;
    categoryImageItem?: GridItemProps;
    categoryImageLink?: LinkPresentationProps;
    categoryImage?: LazyImageProps;
    categoryNameLinkItem?: GridItemProps;
    categoryNameLink?: LinkProps;
    subCategoryLink?: LinkProps;
    viewMoreLinkItem?: GridItemProps;
    // XNG-Changes
    container?: GridContainerProps;
    subCategoryItem?: GridItemProps;
    innerContainer?: GridContainerProps;
    subCategoryImageItem?: GridItemProps;
    subCategoryImageClickable?: ClickablePresentationProps;
    subCategoryImage?: LazyImageProps;
    subCategoryNameLinkItem?: GridItemProps;
    subCategoryNameLink?: LinkPresentationProps;
    categoryContainer?: InjectableCss;
    categoryHeader?: InjectableCss;
    viewMoreLinkWrapper?: InjectableCss;
    viewMoreLink?: LinkPresentationProps;
}

export const categoriesStyles: BrandDetailsCategoriesStyles = {};

const styles = categoriesStyles;

const BrandDetailsCategories: FC<Props> = ({ fields }) => {
    const { isLoading, value: brandCategories } = useContext(BrandCategoriesStateContext);
    if (isLoading || !brandCategories) {
        return null;
    }
    const { title, showImages, gridType, subCategoriesToShow } = fields;
    const categoryItemStyles = gridType === "row" ? styles.categoryItemRow : styles.categoryItemColumn;

    const renderViewMoreLink = (category: BrandCategoryModel) => {
        if (subCategoriesToShow === 0 || (category.subCategories || []).length <= subCategoriesToShow) {
            return null;
        }
        return (
            <StyledWrapper {...styles.viewMoreLinkWrapper}>
                <Link href={category.productListPagePath} aria-label="View More" {...styles.viewMoreLink}>
                    {translate("View More")}
                </Link>
            </StyledWrapper>
        );
    };

    // XNG-Changes: structure update
    return (
        <StyledWrapper>
            <Typography {...styles.title} data-test-selector="brandCategoriesTitle">
                {title}
            </Typography>
            <GridContainer {...styles.container}>
                {brandCategories!.map(category => (
                    <>
                        <GridItem {...styles.subCategoryItem}>
                            <StyledWrapper {...styles.innerContainer}>
                                <StyledWrapper {...styles.categoryContainer}>
                                    <StyledWrapper
                                        {...styles.categoryHeader}
                                        className={category.subCategories?.length ? "has-subcategories" : ""}
                                    >
                                        {showImages && category.featuredImagePath && (
                                            <Clickable
                                                href={category.productListPagePath}
                                                {...styles.subCategoryImageClickable}
                                            >
                                                <LazyImage
                                                    src={category.featuredImagePath}
                                                    altText={category.featuredImageAltText}
                                                    {...styles.subCategoryImage}
                                                />
                                            </Clickable>
                                        )}
                                        <Link
                                            href={category.productListPagePath}
                                            {...styles.subCategoryNameLink}
                                            aria-label={category.categoryShortDescription}
                                        >
                                            {category.categoryShortDescription}
                                        </Link>
                                    </StyledWrapper>
                                    <GridContainer {...categoryDetailLinkListStyles.container}>
                                        {category.subCategories?.slice(0, subCategoriesToShow).map(subCategory => (
                                            <GridItem
                                                key={subCategory.categoryId}
                                                {...categoryDetailLinkListStyles.subCategoryNameLinkItem}
                                            >
                                                <Link
                                                    href={subCategory.productListPagePath}
                                                    {...categoryDetailLinkListStyles.subCategoryNameLink}
                                                    data-test-selector={`brandCategoriesSubCategoryLink_${subCategory.categoryId}`}
                                                    aria-label={subCategory.categoryName}
                                                >
                                                    {subCategory.categoryName}
                                                </Link>
                                            </GridItem>
                                        ))}
                                    </GridContainer>
                                </StyledWrapper>

                                <StyledWrapper {...styles.viewMoreLinkWrapper}>
                                    <Link
                                        href={category.productListPagePath}
                                        {...styles.viewMoreLink}
                                        aria-label="View All"
                                    >
                                        {translate("View All")}
                                    </Link>
                                </StyledWrapper>
                            </StyledWrapper>
                        </GridItem>
                    </>
                ))}
            </GridContainer>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(BrandDetailsCategories),
    definition: {
        group: "Brand Details",
        displayName: "Categories",
        allowedContexts: ["BrandDetailsPage"],
        fieldDefinitions: [
            {
                name: fields.title,
                displayName: "Title",
                editorTemplate: "TextField",
                defaultValue: "Shop By Category",
                fieldType: "Translatable",
            },
            {
                name: fields.showImages,
                displayName: "Show Images",
                editorTemplate: "CheckboxField",
                defaultValue: true,
                fieldType: "General",
            },
            {
                name: fields.subCategoriesToShow,
                displayName: "Sub Categories To Show",
                editorTemplate: "IntegerField",
                defaultValue: 4,
                fieldType: "General",
            },
            {
                name: fields.gridType,
                displayName: "Grid Type",
                editorTemplate: "DropDownField",
                defaultValue: "row",
                fieldType: "General",
                options: [
                    { value: "row", displayName: "Row" },
                    { value: "column", displayName: "Column" },
                ],
            },
        ],
    },
};

export default widgetModule;
